import { useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Layout from "./components/layout/Layout";
import Dashboard from "./components/dashboard/Dashboard";
import Profile from "./components/profile/Profile";
import ChangePassword from "./components/profile/ChangePassword";
import Patients from "./components/patients/Patients";
import NewPatient from "./components/patients/NewPatient";
import EditPatient from "./components/patients/EditPatient";
import PatientDetails from "./components/patients/PatientDetails";
import Appointments from "./components/appointments/Appointments";
import NewAppointment from "./components/appointments/NewAppointment";
import EditAppointment from "./components/appointments/EditAppointment";
import CalendarBlocks from "./components/appointments/CalendarBlocks";
import CalendarBlockForm from "./components/appointments/CalendarBlockForm";
import Clinic from "./components/patients/Clinic";
import Procedures from "./components/procedures/Procedures";
import Supplies from "./components/supplies/Supplies";
import LaboratoryOrderForm from "./components/laboratory/LaboratoryOrderForm";
import LaboratoryOrders from "./components/laboratory/LaboratoryOrders";
import Farmacos from "./components/farmacos/Farmacos";
import PaymentForms from "./components/payments/PaymentForms";
import Locations from "./components/locations/Locations";
import Providers from "./components/providers/Providers";
import ProviderForm from "./components/providers/ProviderForm";
import ProviderDetails from "./components/providers/ProviderDetails";
import Roles from "./components/roles/Roles";
import RoleForm from "./components/roles/RoleForm";
import Insurances from "./components/insurances/Insurances";
import Reports from "./components/reports/Reports";
import FinancialReports from "./components/reports/FinancialReports";
import { NonUserRoute, PermissionRoute, MedicalDataRoute } from "./components/common/ProtectedRoute";
import { MEDICAL_DATA_PERMISSION } from "./utils/medicalData";
import AppointmentReports from "./components/reports/AppointmentReports";
import ActivityLogs from "./components/reports/ActivityLogs";
import Preferences from "./components/preferences/Preferences";
import BillingConfig from "./components/preferences/BillingConfig";
import DentalNotes from "./components/patients/dentalNotes/DentalNotes";
import OdontogramTabs from "./components/patients/dentalNotes/OdontogramTabs";
import ViewDischargeOdontogram from "./components/patients/dentalNotes/ViewDischargeOdontogram";
import NewDentalNote from "./components/patients/dentalNotes/NewDentalNote";
import EditDentalNote from "./components/patients/dentalNotes/EditDentalNote";
import DentalChart from "./components/patients/dentalNotes/DentalChart";
import PatientAppointments from "./components/appointments/PatientAppointments";
import PatientPhotos from "./components/patients/photos/PatientPhotos";
import MedicalHistory from "./components/patients/medicalHistory/MedicalHistory";
import PatientPayments from "./components/patients/payments/PatientPayments";
import NewPayment from "./components/patients/payments/NewPayment";
import PatientBalance from "./components/patients/balance/PatientBalance";
import Prescriptions from "./components/patients/prescriptions/Prescriptions";
import NewPrescription from "./components/patients/prescriptions/NewPrescription";
import ViewPrescription from "./components/patients/prescriptions/ViewPrescription";
import EditPrescription from "./components/patients/prescriptions/EditPrescription";
import EvolutionNotes from "./components/patients/evolutionNotes/EvolutionNotes";
import NewEvolutionNote from "./components/patients/evolutionNotes/NewEvolutionNote";
import EditEvolutionNote from "./components/patients/evolutionNotes/EditEvolutionNote";
import DentalCertificates from "./components/patients/certificates/DentalCertificates";
import NewCertificate from "./components/patients/certificates/NewCertificate";
import EditCertificate from "./components/patients/certificates/EditCertificate";
import TreatmentPlan from "./components/patients/treatmentPlan/TreatmentPlan";
import TreatmentPlansList from "./components/patients/treatmentPlan/TreatmentPlansList";
import TreatmentPlanInvoice from "./components/patients/treatmentPlan/TreatmentPlanInvoice";
import Diagnoses from "./components/diagnoses/Diagnoses";
import Integrations from "./components/integrations/Integrations";
import SubscriptionPage from "./components/subscription/SubscriptionPage";
import ThankYouPage from "./components/subscription/ThankYouPage";
import WelcomeScreen from "./components/welcome/WelcomeScreen";
import LandingScreen from "./components/welcome/LandingScreen";
import OrganizationsDirectory from "./components/public/OrganizationsDirectory";
import OrganizationPage from "./components/public/OrganizationPage";
import OrganizationUsers from "./components/organizationUsers/OrganizationUsers";
import UserDetails from "./components/organizationUsers/UserDetails";
import UpdatesPage from "./components/updates/UpdatesPage";
import Ayuda from "./components/ayuda/Ayuda";
import NewSupportCase from "./components/ayuda/NewSupportCase";
import SupportCaseDetail from "./components/ayuda/SupportCaseDetail";
import SetupPassword from "./components/auth/SetupPassword";
import AuthCallback from "./components/auth/AuthCallback";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { useAuthSync } from "./hooks/useAuthSync";
import AppUtilsService from "./services/appUtilsService";
import { initMixpanelTracking, identifyMixpanelUser, trackViewVisited } from "./services/mixpanelTrackingService";
import "./App.css";
import "./styles/buttons.css";
import "./styles/modal.css";

// Patient clinical pages: require the medical data permission, which is denied to everyone
// when the organization does not have medical data enabled.
const ClinicalRoute = ({ children }) => (
  <PermissionRoute permission={MEDICAL_DATA_PERMISSION} redirectTo="/patients/list">
    {children}
  </PermissionRoute>
);

function RouteAnalyticsTracker({ user }) {
  const location = useLocation();
  const lastTrackedRoute = useRef("");

  useEffect(() => {
    initMixpanelTracking();
  }, []);

  useEffect(() => {
    identifyMixpanelUser(user);
  }, [user]);

  useEffect(() => {
    const currentRoute = `${location.pathname}${location.search}`;
    if (lastTrackedRoute.current === currentRoute) {
      return;
    }

    trackViewVisited(location.pathname, {
      query_string: location.search || undefined,
      current_url: window.location.href
    });

    lastTrackedRoute.current = currentRoute;
  }, [location.pathname, location.search]);

  return null;
}

function App() {
  const auth = useAuth();
  const { user, isLoading: userLoading, error: userError, signOutLocal } = useAuthSync();

  const signOutRedirect = () => {
    const clientId = "2furhq1s6v5mbj77uafsr7j660";
    sessionStorage.clear();

    // Clear user data from localStorage and auth store
    signOutLocal();
    AppUtilsService.clearUserData();

    const logoutUri = process.env.REACT_APP_BASE_DOMAIN || window.location.origin;
    const cognitoDomain = "https://us-east-1tohvlurxq.auth.us-east-1.amazoncognito.com";
    window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
  };

  if (auth.isLoading || (!user && userLoading)) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Cargando...</p>
      </div>
    );
  }

  if (auth.error) {
    return (
      <div className="error-container">
        <h2>Error de Autenticación</h2>
        <p>{auth.error.message}</p>
        <button onClick={() => auth.signinRedirect()}>Intentar de nuevo</button>
      </div>
    );
  }

  if (userError) {
    return (
      <div className="error-container">
        <h2>Error de Usuario</h2>
        <p>No se pudo cargar o crear el perfil de usuario: {userError.message}</p>
        <button onClick={() => window.location.reload()}>Reintentar</button>
      </div>
    );
  }

  if (auth.isAuthenticated) {
    // Wait for user data to load before deciding which route to show
    if (!user && !userError) {
      return (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Cargando perfil...</p>
        </div>
      );
    }

    // Check if user has accepted TOS
    // If user exists but hasn't accepted TOS, show landing screen
    // WITHOUT SubscriptionProvider to avoid loading subscription data
    if (user && !user.tosAccepted) {
      return (
        <Router>
          <RouteAnalyticsTracker user={user} />
          <Routes>
            <Route path="/landing" element={<LandingScreen />} />
            <Route path="*" element={<Navigate to="/landing" replace />} />
          </Routes>
        </Router>
      );
    }

    // User has accepted TOS, load full app with SubscriptionProvider
    return (
      <SubscriptionProvider>
        <Router>
          <RouteAnalyticsTracker user={user} />
          <Layout signOutRedirect={signOutRedirect}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/patients/list" element={<Patients />} />
              <Route path="/patients/new" element={<NewPatient />} />
              <Route path="/patients/:patientId/edit" element={<EditPatient />} />
              <Route path="/patients/:patientId" element={<PatientDetails />} />
              {/* New odontogram experience */}
              <Route path="/patients/:patientId/dental-notes" element={<ClinicalRoute><OdontogramTabs /></ClinicalRoute>} />
              <Route path="/patients/:patientId/dental-notes/discharge/:dischargeId" element={<ClinicalRoute><ViewDischargeOdontogram /></ClinicalRoute>} />

              {/* Legacy dental notes/odontogram view */}
              <Route path="/patients/:patientId/dental-notes/legacy" element={<ClinicalRoute><DentalNotes /></ClinicalRoute>} />
              <Route path="/patients/:patientId/dental-notes/new" element={<ClinicalRoute><NewDentalNote /></ClinicalRoute>} />
              <Route path="/patients/:patientId/dental-notes/:noteId/edit" element={<ClinicalRoute><EditDentalNote /></ClinicalRoute>} />
              <Route path="/patients/:patientId/dental-chart" element={<ClinicalRoute><DentalChart /></ClinicalRoute>} />
              <Route path="/patients/:patientId/appointments" element={<PatientAppointments />} />
              <Route path="/patients/:patientId/photos" element={<ClinicalRoute><PatientPhotos /></ClinicalRoute>} />
              <Route path="/patients/:patientId/medical-history" element={<ClinicalRoute><MedicalHistory /></ClinicalRoute>} />
              <Route path="/patients/:patientId/payments" element={<PatientPayments />} />
              <Route path="/patients/:patientId/balance" element={<PatientBalance />} />
              <Route path="/patients/:patientId/prescriptions" element={<ClinicalRoute><Prescriptions /></ClinicalRoute>} />
              <Route path="/patients/:patientId/prescriptions/new" element={<ClinicalRoute><NewPrescription /></ClinicalRoute>} />
              <Route path="/patients/:patientId/prescriptions/:prescriptionId" element={<ClinicalRoute><ViewPrescription /></ClinicalRoute>} />
              <Route path="/patients/:patientId/prescriptions/:prescriptionId/edit" element={<ClinicalRoute><EditPrescription /></ClinicalRoute>} />
              <Route path="/patients/:patientId/evolution-notes" element={<ClinicalRoute><EvolutionNotes /></ClinicalRoute>} />
              <Route path="/patients/:patientId/evolution-notes/new" element={<ClinicalRoute><NewEvolutionNote /></ClinicalRoute>} />
              <Route path="/patients/:patientId/evolution-notes/:noteId/edit" element={<ClinicalRoute><EditEvolutionNote /></ClinicalRoute>} />
              <Route path="/patients/:patientId/certificates" element={<ClinicalRoute><DentalCertificates /></ClinicalRoute>} />
              <Route path="/patients/:patientId/certificates/new" element={<ClinicalRoute><NewCertificate /></ClinicalRoute>} />
              <Route path="/patients/:patientId/certificates/:certificateId/edit" element={<ClinicalRoute><EditCertificate /></ClinicalRoute>} />
              <Route path="/patients/:patientId/treatment-plans" element={<TreatmentPlansList />} />
              <Route path="/patients/:patientId/treatment-plan" element={<TreatmentPlan />} />
              <Route path="/patients/:patientId/treatment-plans/:planId" element={<TreatmentPlan />} />
              <Route path="/patients/:patientId/treatment-plans/:planId/edit" element={<TreatmentPlan />} />
              <Route path="/patients/:patientId/treatment-plans/:planId/invoice" element={<TreatmentPlanInvoice />} />
              <Route path="/payments/new" element={<NewPayment />} />
              <Route path="/appointments" element={<Appointments />} />
              <Route path="/appointments/new" element={<NewAppointment />} />
              <Route path="/appointments/edit/:appointmentId" element={<EditAppointment />} />
              <Route path="/appointments/blocks" element={<CalendarBlocks />} />
              <Route path="/appointments/blocks/:doctorId/new" element={<CalendarBlockForm />} />
              <Route path="/appointments/blocks/:doctorId/:blockId/edit" element={<CalendarBlockForm />} />
              <Route path="/settings/profile" element={<Profile />} />
              <Route path="/settings/profile/change-password" element={<ChangePassword />} />
              <Route path="/settings/subscription" element={
                <NonUserRoute>
                  <SubscriptionPage />
                </NonUserRoute>
              } />
              <Route path="/subscription/thank-you" element={
                <NonUserRoute>
                  <ThankYouPage />
                </NonUserRoute>
              } />
              <Route path="/settings/clinic" element={<Clinic />} />
              <Route path="/settings/users" element={<OrganizationUsers />} />
              <Route path="/settings/users/:userId" element={<UserDetails />} />
              <Route path="/settings/procedures" element={<Procedures />} />
              <Route path="/supplies" element={<Supplies />} />
              <Route path="/laboratory-orders" element={
                <NonUserRoute>
                  <LaboratoryOrders />
                </NonUserRoute>
              } />
              <Route path="/laboratory-orders/new" element={
                <NonUserRoute>
                  <LaboratoryOrderForm />
                </NonUserRoute>
              } />
              <Route path="/laboratory-orders/:orderId/edit" element={
                <NonUserRoute>
                  <LaboratoryOrderForm />
                </NonUserRoute>
              } />
              <Route path="/settings/farmacos" element={<MedicalDataRoute><Farmacos /></MedicalDataRoute>} />
              <Route path="/settings/payment-forms" element={
                <NonUserRoute>
                  <PaymentForms />
                </NonUserRoute>
              } />
              <Route path="/settings/providers" element={
                <NonUserRoute>
                  <Providers />
                </NonUserRoute>
              } />
              <Route path="/laboratory-orders/providers" element={
                <NonUserRoute>
                  <Providers />
                </NonUserRoute>
              } />
              <Route path="/settings/providers/new" element={
                <NonUserRoute>
                  <ProviderForm />
                </NonUserRoute>
              } />
              <Route path="/laboratory-orders/providers/new" element={
                <NonUserRoute>
                  <ProviderForm />
                </NonUserRoute>
              } />
              <Route path="/settings/providers/:providerId" element={
                <NonUserRoute>
                  <ProviderDetails />
                </NonUserRoute>
              } />
              <Route path="/laboratory-orders/providers/:providerId" element={
                <NonUserRoute>
                  <ProviderDetails />
                </NonUserRoute>
              } />
              <Route path="/settings/providers/:providerId/edit" element={
                <NonUserRoute>
                  <ProviderForm />
                </NonUserRoute>
              } />
              <Route path="/laboratory-orders/providers/:providerId/edit" element={
                <NonUserRoute>
                  <ProviderForm />
                </NonUserRoute>
              } />
              <Route path="/settings/roles" element={
                <NonUserRoute>
                  <Roles />
                </NonUserRoute>
              } />
              <Route path="/settings/roles/new" element={
                <NonUserRoute>
                  <RoleForm />
                </NonUserRoute>
              } />
              <Route path="/settings/roles/:roleId/edit" element={
                <NonUserRoute>
                  <RoleForm />
                </NonUserRoute>
              } />
              <Route path="/settings/locations" element={
                <NonUserRoute>
                  <Locations />
                </NonUserRoute>
              } />
              <Route path="/settings/insurance" element={<Insurances />} />
              <Route path="/settings/preferences" element={<Preferences />} />
              <Route path="/settings/billing" element={<BillingConfig />} />
              <Route path="/settings/diagnoses" element={<MedicalDataRoute><Diagnoses /></MedicalDataRoute>} />
              <Route path="/integrations" element={<Integrations />} />
              <Route path="/reports" element={
                <Reports />
              } />
              <Route path="/reports/financial" element={
                <PermissionRoute permission="reports:viewfinancial">
                  <FinancialReports />
                </PermissionRoute>
              } />
              <Route path="/reports/appointments" element={
                <PermissionRoute permission="reports:viewappointments">
                  <AppointmentReports />
                </PermissionRoute>
              } />
              <Route path="/reports/activity-logs" element={
                <NonUserRoute>
                  <ActivityLogs />
                </NonUserRoute>
              } />
              <Route path="/updates" element={<UpdatesPage />} />
              <Route path="/ayuda" element={<Ayuda />} />
              <Route path="/ayuda/solicitar" element={<NewSupportCase />} />
              <Route path="/ayuda/casos/:caseId" element={<SupportCaseDetail />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Layout>
        </Router>
      </SubscriptionProvider>
    );
  }

  // Public routes that don't require authentication
  return (
    <Router>
      <RouteAnalyticsTracker user={null} />
      <Routes>
        <Route path="/directory" element={<OrganizationsDirectory />} />
        <Route path="/directory/:organizationId" element={<OrganizationPage />} />
        <Route path="/cb" element={<AuthCallback />} />
        <Route path="/auth/setup-password" element={<SetupPassword />} />
        <Route path="*" element={<WelcomeScreen onLogin={() => auth.signinRedirect()} />} />
      </Routes>
    </Router>
  );
}

export default App;